/**
 * Start the plaintext migration a little later, and not on the same tick as
 * everyone else in the room.
 *
 * Two of the three enrolment triggers fire INSIDE a lesson — a highlight save,
 * and the first character typed into a blank — at the moment a roomful of
 * members are on the same page. Each of them may be the moment a member comes
 * to hold their key, and the walk that follows re-writes every plaintext row
 * the member has, in batches of twenty. Started on the tick that triggered it,
 * a service would put hundreds of those walks on the API in the same second,
 * on top of the busiest write hour it has. Spread them: five seconds before
 * anything, so the walk is never inside the burst that triggered it, plus up
 * to twenty-five more.
 *
 * The Hub open, the third trigger, starts the walk at once and is not routed
 * through here: it is a deliberate page-level event with its own natural
 * spread, the Hub header shows the walk's progress line on that open (spec
 * 6.7), and the migration E2E waits on exactly that.
 *
 * ONLY FOR A MEMBER WHO HOLDS THE KEY. `migratePlaintextIfNeeded` skips for
 * anyone else (`NOT_ENROLLED`, `NO_KEY`), so scheduling for them would arm a
 * timer that does nothing — and the check is what makes calling this on EVERY
 * trigger cheap enough: an enrolled member whose rows are already sealed costs
 * one getter read here and one `migratedAt` read when the timer fires.
 * Calling it on every trigger, not only on a fresh `ENROLLED` outcome, is the
 * point: a member who enrols by typing and never opens the Hub used to get
 * exactly one attempt, on the enrolling keystroke, and if that one landed on
 * `OUTBOX_PENDING` or no network their old rows stayed in the clear for good.
 */
import { migratePlaintextIfNeeded } from './migration';

export const MIGRATION_JITTER_MIN_MS = 5_000;
export const MIGRATION_JITTER_SPREAD_MS = 25_000;

/**
 * @param {{ store: object }} args
 * @returns {boolean} whether a walk was scheduled
 */
export function scheduleMigration({ store } = {}) {
  if (store?.getters?.['courses-keys/hasMasterKey'] !== true) return false;
  setTimeout(
    () => {
      migratePlaintextIfNeeded({ store }).catch(error => {
        console.warn('[CoursesE2EE] The plaintext migration could not start:', error);
      });
    },
    MIGRATION_JITTER_MIN_MS + Math.random() * MIGRATION_JITTER_SPREAD_MS,
  );
  return true;
}
